import type { AeaExerciseModel, LanguageInfo, UserSubject } from "p6m-p6u";
import { getLanguageName, getLanguageNameFromCode, UNKNOWN_LANGUAGE_NAME } from "./LanguageCountryName";
import type { LanguageLookupMaps } from "../complex/classActivityCard/ClassActivityCardsOverview.types";

// German is always the base/native language in this app, so it's never the interesting
// "foreign" language to filter by. When the answer language is German ("Deutsch" as display
// name, "de" as language code), we show the other language instead.
const GERMAN_DISPLAY_NAME = "Deutsch";
const GERMAN_LANG_CODE = "de";

/** Structural, so both the teacher's test and the student's copy of it can be read. */
export interface TestLanguageNameParams {
    language?: LanguageInfo;
    ownSubjectInfo?: UserSubject;
}

export const getTestLanguageName = (test: TestLanguageNameParams): string | undefined => {
    if (test.language?.answerLanguageName) {
        const langName = test.language.answerLanguageName;
        if (langName === GERMAN_DISPLAY_NAME) {
            return test.language.questionLanguageName;
        }
        return langName;
    }
    if (test.ownSubjectInfo?.primaryLang) {
        const langCode = test.ownSubjectInfo.primaryLang;
        if (langCode === GERMAN_LANG_CODE && test.ownSubjectInfo.secondaryLang) {
            return getLanguageNameFromCode(test.ownSubjectInfo.secondaryLang);
        }
        return getLanguageNameFromCode(langCode);
    }
    return undefined;
};

/**
 * The language to print beside a test, as opposed to the one to filter by.
 *
 * `getTestLanguageName` answers a teacher-side dropdown, where the map's "Other" bucket and a
 * German fallback are useful groupings. On a student's result they are noise: an untranslated
 * English word, or the very base language the label exists to skip. Nothing beats no label here,
 * and the result page drops the whole line when this is empty.
 */
export const getDisplayableTestLanguageName = (test: TestLanguageNameParams): string => {
    const languageName = getTestLanguageName(test);

    if (!languageName || languageName === UNKNOWN_LANGUAGE_NAME || languageName === GERMAN_DISPLAY_NAME) {
        return "";
    }

    return languageName;
};

export const getVocabTrainingLanguageName = (vocabTraining: AeaExerciseModel, maps: LanguageLookupMaps): string | undefined => {
    const subjectId = vocabTraining.subjectContentId?.id;
    if (!subjectId) return undefined;

    const ownInfo = maps.userUsedOwnSubjectsInfo[subjectId];
    if (ownInfo?.primaryLang) {
        if (ownInfo.primaryLang === GERMAN_LANG_CODE && ownInfo.secondaryLang) {
            return getLanguageName(ownInfo.secondaryLang);
        }
        return getLanguageName(ownInfo.primaryLang);
    }

    const bookInfo = maps.userUsedBooksInfo[subjectId];
    if (bookInfo?.band?.Language) {
        const answerLang = bookInfo.band.Language.answerLanguageName;
        if (answerLang === GERMAN_DISPLAY_NAME) {
            return bookInfo.band.Language.questionLanguageName;
        }
        return answerLang;
    }
    return undefined;
};
